import { useEffect, useState, useRef } from 'react';
import { useAuth, useSignIn, useUser } from '@clerk/clerk-react';
import { AutoLoginCoordinator } from './AutoLoginCoordinator';
import { AuthEvent, AuthState } from './types';
import { EventLogPanel } from './components/EventLogPanel';
import { LoadingState } from './components/LoadingState';
import { SuccessState } from './components/SuccessState';
import { MagicLinkAuth } from './components/MagicLinkAuth';

export const AutoLoginPage = () => {
  const { isSignedIn, userId } = useAuth();
  const { user } = useUser();
  const { signIn } = useSignIn();

  const [state, setState] = useState<AuthState>('detecting');
  const [events, setEvents] = useState<AuthEvent[]>([]);
  const [authMethod, setAuthMethod] = useState<string>('unknown');

  const hasExecutedRef = useRef(false);
  const wasSignedInOnMountRef = useRef(isSignedIn);
  const hasLoggedClerkInitRef = useRef(false);

  // Handle auth success
  useEffect(() => {
    if (isSignedIn && userId) {
      setState('success');

      // Determine the auth method from events
      const successEvent = events.find((e) => e.stage === 'platform_attempt' && e.status === 'success');
      const osSuccessEvent = events.find((e) => e.stage === 'os_attempt' && e.status === 'success');
      const magicLinkEvent = events.find((e) => e.stage === 'fallback' && e.message.includes('Magic link sent'));

      let method = 'Manual';
      if (successEvent && successEvent.details?.platform) {
        method = `${successEvent.details.platform} OAuth`;
      } else if (osSuccessEvent && osSuccessEvent.details?.provider) {
        method = `${osSuccessEvent.details.provider} OAuth (OS)`;
      } else if (magicLinkEvent) {
        method = 'Magic Link (Email)';
      } else if (wasSignedInOnMountRef.current) {
        method = 'Already signed in';
      }

      setAuthMethod(method);

      // Add success event if not already in auth flow
      if (!events.find((e) => e.stage === 'success')) {
        setEvents((prev) => [
          ...prev,
          {
            timestamp: new Date(),
            stage: 'success',
            status: 'success',
            message: `Authentication successful - ${method}`,
            details: { userId, email: user?.primaryEmailAddress?.emailAddress },
          },
        ]);
      }
    }
  }, [isSignedIn, userId, user, events]);

  // Log when waiting for Clerk to initialize
  useEffect(() => {
    if (!signIn && !hasLoggedClerkInitRef.current && !isSignedIn) {
      hasLoggedClerkInitRef.current = true;
      setEvents((prev) => [
        ...prev,
        {
          timestamp: new Date(),
          stage: 'detection',
          status: 'info',
          message: 'Waiting for Clerk authentication to initialize...',
        },
      ]);
      console.log('[AutoLogin] ⏳ Waiting for Clerk SignIn object to initialize');
    }
  }, [signIn, isSignedIn]);

  // Execute auth chain on mount (wait for signIn to be ready)
  useEffect(() => {
    // Don't execute if already executed, already signed in, or signIn is not ready yet
    if (hasExecutedRef.current || isSignedIn || !signIn) return;
    hasExecutedRef.current = true;

    setEvents((prev) => [
      ...prev,
      {
        timestamp: new Date(),
        stage: 'detection',
        status: 'success',
        message: 'Clerk authentication initialized - starting auto-login detection',
      },
    ]);

    console.log('[AutoLogin] ✅ SignIn object ready, executing auth chain');

    const coordinator = new AutoLoginCoordinator(signIn, (event) => {
      setEvents((prev) => [...prev, event]);

      // Update state based on event stage
      if (event.stage === 'platform_attempt') {
        setState('attempting_platform');
      } else if (event.stage === 'os_attempt') {
        setState('attempting_os');
      } else if (event.stage === 'fallback') {
        setState('fallback');
      }
    });

    coordinator.executeAuthChain();
  }, [signIn, isSignedIn]);

  return (
    <div className='min-h-screen bg-gray-950'>
      <EventLogPanel events={events} />

      <div className='pt-48'>
        {state === 'detecting' && (
          <LoadingState message={signIn ? 'Detecting authentication method...' : 'Initializing Clerk authentication...'} />
        )}

        {state === 'attempting_platform' && <LoadingState message='Attempting platform authentication...' />}

        {state === 'attempting_os' && <LoadingState message='Attempting OS authentication...' />}

        {state === 'fallback' && (
          <MagicLinkAuth
            onEvent={(message, status) => {
              setEvents((prev) => [
                ...prev,
                {
                  timestamp: new Date(),
                  stage: 'fallback',
                  status,
                  message,
                },
              ]);
            }}
          />
        )}

        {state === 'success' && userId && (
          <SuccessState userId={userId} email={user?.primaryEmailAddress?.emailAddress} method={authMethod} />
        )}
      </div>
    </div>
  );
};
